import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { MoneyInput } from './MoneyInput';
import { ArrowDownCircle, Plus, Trash2, Wallet, Printer, CheckCircle2, HandCoins, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useTreasurySubmit, idempotencyHeader } from '../lib/useTreasurySubmit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);
const fmtCurrency = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isDeferredCheck = (name) => {
  const n = (name || '').toLowerCase().replace(/[.\-\s]/g, '');
  return n.includes('diferid') || n.includes('echeq');
};

// Diálogo dedicado de Orden de Pago a Proveedor (solo cancelación de deuda de cuenta corriente).
// A diferencia de la OP de Tesorería, NO permite pagos varios (destinatario "otro"),
// ni anticipos, ni imputación a concepto de gasto: siempre imputa a comprobantes pendientes.
export function SupplierPaymentOrderDialog({ open, onOpenChange, supplierId, supplierName, onSaved }) {
  const [cashBanks, setCashBanks] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [pending, setPending] = useState([]);
  // v3.38.3 — Saldo a favor del proveedor (anticipos de OPs + NCs sin netear)
  const [availableAdvance, setAvailableAdvance] = useState(0);
  const [availableNcCredit, setAvailableNcCredit] = useState(0);
  const [applyingAdvance, setApplyingAdvance] = useState(false);
  const [form, setForm] = useState({
    date: today(), cash_bank_id: '', description: '',
    payment_details: [{ payment_type_id: '', amount: '', reference: '', due_date: '' }],
  });
  const [saving, setSaving] = useState(false);
  // v3.22.5: al guardar la OP, en lugar de cerrar el diálogo, mostramos una vista de éxito
  // con botones para imprimir el PDF o volver al listado.
  const [createdOP, setCreatedOP] = useState(null);

  const reset = useCallback(() => {
    setForm({ date: today(), cash_bank_id: '', description: '', payment_details: [{ payment_type_id: '', amount: '', reference: '', due_date: '' }] });
    setPending([]);
    setAvailableAdvance(0);
    setAvailableNcCredit(0);
    setCreatedOP(null);
  }, []);

  // Función reutilizable para (re)cargar los pendientes y los saldos a favor
  const loadPending = useCallback(async () => {
    if (!supplierId) return;
    const r = await axios.get(
      `${BACKEND_URL}/api/supplier-accounts/${supplierId}/pending?include_advance=true`,
      { withCredentials: true }
    );
    const body = r.data || {};
    setPending((body.pending || []).map(p => ({ ...p, selected: false, payAmount: p.outstanding })));
    setAvailableAdvance(Number(body.available_advance_total || 0));
    setAvailableNcCredit(Number(body.available_nc_credit_total || 0));
  }, [supplierId]);

  useEffect(() => {
    if (!open || !supplierId) return;
    reset();
    (async () => {
      try {
        const [cb, pt] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/treasury/cash-banks/active`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/treasury/payment-types/active`, { withCredentials: true }),
        ]);
        setCashBanks(cb.data || []);
        setPaymentTypes(pt.data || []);
        await loadPending();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Error al cargar datos del proveedor');
      }
    })();
  }, [open, supplierId, reset, loadPending]);

  // v3.38.3 — Aplica automáticamente el saldo a favor (anticipos de OPs) a las FCs pendientes
  // en orden FIFO (comprobante más antiguo primero). Netea los `outstanding` para que el
  // operador vea los saldos reales a pagar y no se confunda.
  const handleApplyAdvance = async () => {
    if (availableAdvance <= 0) return;
    setApplyingAdvance(true);
    try {
      const sortedPending = [...pending].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      let remaining = availableAdvance;
      const allocations = [];
      for (const p of sortedPending) {
        if (remaining <= 0.009) break;
        const amount = Math.min(remaining, Number(p.outstanding || 0));
        if (amount > 0.009) {
          allocations.push({ purchase_invoice_id: p.purchase_invoice_id, amount: Math.round(amount * 100) / 100 });
          remaining -= amount;
        }
      }
      if (allocations.length === 0) {
        toast.info('No hay facturas pendientes a las que aplicar el saldo a favor');
        return;
      }
      await axios.post(
        `${BACKEND_URL}/api/supplier-accounts/${supplierId}/apply-advance`,
        { allocations, date: form.date },
        { withCredentials: true }
      );
      const applied = allocations.reduce((s, a) => s + a.amount, 0);
      toast.success(`Se aplicaron ${fmtCurrency(applied)} de saldo a favor`);
      await loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo aplicar el saldo a favor');
    } finally {
      setApplyingAdvance(false);
    }
  };

  const togglePending = (pid) => setPending(prev => prev.map(p => p.purchase_invoice_id === pid ? { ...p, selected: !p.selected } : p));
  const updatePendingAmount = (pid, val) => setPending(prev => prev.map(p => p.purchase_invoice_id === pid ? { ...p, payAmount: val } : p));
  const allocationsTotal = pending.filter(p => p.selected).reduce((s, p) => s + (parseFloat(p.payAmount) || 0), 0);

  // v3.36.4 — Filtrar payment_types según categorías permitidas del cash_bank seleccionado
  const selectedCashBank = cashBanks.find(cb => cb.cash_bank_id === form.cash_bank_id);
  const allowedCats = selectedCashBank?.allowed_payment_categories ?? null;
  const availablePaymentTypes = (paymentTypes || []).filter(pt => {
    if (allowedCats === null) return true;
    if (allowedCats.length === 0) return false;
    return allowedCats.includes((pt.category || '').toLowerCase());
  });
  const addPD = () => setForm(f => ({ ...f, payment_details: [...f.payment_details, { payment_type_id: '', amount: '', reference: '', due_date: '' }] }));
  const updatePD = (i, field, val) => setForm(f => { const d = [...f.payment_details]; d[i] = { ...d[i], [field]: val }; return { ...f, payment_details: d }; });
  const removePD = (i) => setForm(f => f.payment_details.length <= 1 ? f : ({ ...f, payment_details: f.payment_details.filter((_, idx) => idx !== i) }));
  const payTotal = form.payment_details.reduce((s, pd) => s + (parseFloat(pd.amount) || 0), 0);
  const submit = useTreasurySubmit();

  const handleSave = async () => {
    return submit.run(async (idemKey) => {
    const validDetails = form.payment_details.filter(pd => pd.payment_type_id && pd.amount);
    if (!form.cash_bank_id || validDetails.length === 0) {
      toast.error('Elegí la caja/banco y al menos un medio de pago'); return;
    }
    const selected = pending.filter(p => p.selected && parseFloat(p.payAmount) > 0);
    for (const p of selected) {
      if (parseFloat(p.payAmount) > p.outstanding + 0.01) { toast.error(`El importe a cancelar de ${p.label} supera su saldo`); return; }
    }
    for (const pd of validDetails) {
      const ptName = paymentTypes.find(pt => pt.payment_type_id === pd.payment_type_id)?.name;
      if (isDeferredCheck(ptName) && !pd.due_date) { toast.error('Indicá la fecha de pago del cheque diferido / e.Cheq'); return; }
    }
    const allocTotal = selected.reduce((s, p) => s + parseFloat(p.payAmount), 0);
    if (allocTotal > payTotal + 0.01) {
      toast.error(`Lo asignado a comprobantes (${fmtCurrency(allocTotal)}) supera el total del pago (${fmtCurrency(payTotal)})`); return;
    }
    const payAdvance = Math.max(0, Math.round((payTotal - allocTotal) * 100) / 100);  // v3.37.2

    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/treasury/payment-orders`, {
        date: form.date,
        cash_bank_id: form.cash_bank_id,
        expense_concept_id: null,
        description: form.description,
        recipient_type: 'supplier',
        supplier_id: supplierId,
        recipient_name: supplierName,
        is_advance: false,
        cancel_supplier_debt: true,
        debt_allocations: selected.map(p => ({ purchase_invoice_id: p.purchase_invoice_id, amount: parseFloat(p.payAmount) })),
        payment_details: validDetails.map(pd => ({
          payment_type_id: pd.payment_type_id,
          amount: parseFloat(pd.amount),
          reference: pd.reference || null,
          due_date: pd.due_date || null,
        })),
      }, { withCredentials: true, headers: idempotencyHeader(idemKey) });
      const op = res.data || {};
      toast.success(`OP ${op.payment_order_number || ''} registrada${payAdvance > 0 ? ` · saldo a favor ${fmtCurrency(payAdvance)}` : ''}`);
      setCreatedOP({
        payment_order_id: op.payment_order_id,
        payment_order_number: op.payment_order_number,
        total: op.total_amount || payTotal,
      });
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al registrar la orden de pago');
    } finally { setSaving(false); }
    });
  };

  const closeAll = () => { setCreatedOP(null); onOpenChange(false); };
  const printOP = () => {
    if (!createdOP?.payment_order_id) return;
    window.open(`${BACKEND_URL}/api/treasury/payment-orders/${createdOP.payment_order_id}/pdf?t=${Date.now()}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={createdOP ? closeAll : onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="supplier-op-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {createdOP ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <ArrowDownCircle className="w-5 h-5 text-red-500" />}
            {createdOP ? `OP ${createdOP.payment_order_number} registrada` : 'Nueva Orden de Pago a Proveedor'}
          </DialogTitle>
          <DialogDescription>
            {createdOP
              ? <>El pago quedó imputado a {supplierName} por <b>{fmtCurrency(createdOP.total)}</b>. Podés imprimir la Orden ahora o volver al listado y hacerlo más tarde desde el detalle del proveedor.</>
              : <>Pago imputado a comprobantes pendientes de <b>{supplierName}</b> (baja de cuenta corriente). Si pagás más que lo asignado o no seleccionás ningún comprobante, la diferencia queda como <strong>saldo a favor</strong> del proveedor.</>}
          </DialogDescription>
        </DialogHeader>

        {createdOP ? (
          <div className="py-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Orden de Pago</div>
                <div className="text-2xl font-bold text-emerald-800" data-testid="op-created-number">{createdOP.payment_order_number}</div>
                <div className="text-sm text-muted-foreground mt-1">Total: <b>{fmtCurrency(createdOP.total)}</b> · {supplierName}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={closeAll} data-testid="op-created-close">Cerrar</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={printOP} data-testid="op-created-print">
                  <Printer className="w-4 h-4 mr-1" /> Imprimir OP
                </Button>
              </div>
            </div>
          </div>
        ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha *</Label>
              <Input type="date" max={today()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="supplier-op-date" />
            </div>
            <div>
              <Label>Caja/Banco origen *</Label>
              <select value={form.cash_bank_id} onChange={(e) => setForm({ ...form, cash_bank_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="supplier-op-cashbank">
                <option value="">Seleccionar...</option>
                {cashBanks.map(cb => <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name} ({cb.currency})</option>)}
              </select>
            </div>
          </div>

          <div className="rounded-md border p-3 space-y-2" data-testid="supplier-op-pending-list">
            <Label className="text-sm font-semibold flex items-center gap-1"><Wallet className="w-4 h-4 text-emerald-600" /> Comprobantes pendientes</Label>

            {/* v3.38.3 — Aviso de saldo a favor sin aplicar (anticipos de OPs) + botón para netear */}
            {availableAdvance > 0.009 && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 p-2.5 flex items-start gap-2 text-sm"
                data-testid="supplier-op-advance-banner"
              >
                <HandCoins className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-amber-900">
                    Este proveedor tiene <span data-testid="supplier-op-advance-amount">{fmtCurrency(availableAdvance)}</span> a favor
                  </div>
                  <div className="text-xs text-amber-800">
                    Si querés aplicarlo primero, los saldos de las facturas van a quedar neteados automáticamente.
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-400 text-amber-900 hover:bg-amber-100"
                  onClick={handleApplyAdvance}
                  disabled={applyingAdvance}
                  data-testid="supplier-op-apply-advance"
                >
                  {applyingAdvance ? 'Aplicando…' : 'Aplicar al pago'}
                </Button>
              </div>
            )}

            {/* v3.38.3 — NCs sin netear: solo informativo (hoy no se auto-aplican) */}
            {availableNcCredit > 0.009 && (
              <div
                className="rounded-md border border-sky-200 bg-sky-50/70 p-2 flex items-start gap-2 text-xs"
                data-testid="supplier-op-nc-notice"
              >
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-sky-700 shrink-0" />
                <span className="text-sky-900">
                  Hay <b>{fmtCurrency(availableNcCredit)}</b> en Notas de Crédito del proveedor sin netear.
                  Si corresponde descontarlas, usá <b>Cuenta Corriente → Aplicar a comprobantes</b>.
                </span>
              </div>
            )}

            {pending.length === 0 ? (
              <p className="text-sm text-muted-foreground">Este proveedor no tiene comprobantes pendientes en cuenta corriente.</p>
            ) : pending.map(p => (
              <div key={p.purchase_invoice_id} className="flex items-center gap-2 text-sm" data-testid={`supplier-op-pending-${p.purchase_invoice_id}`}>
                <input type="checkbox" className="h-4 w-4" checked={p.selected} onChange={() => togglePending(p.purchase_invoice_id)} data-testid={`supplier-op-check-${p.purchase_invoice_id}`} />
                <span className="flex-1">{p.label} <span className="text-xs text-muted-foreground">(saldo {fmtCurrency(p.outstanding)})</span></span>
                <MoneyInput className="h-8 w-32" value={p.payAmount} disabled={!p.selected} onChange={(e) => updatePendingAmount(p.purchase_invoice_id, e.target.value)} data-testid={`supplier-op-amount-${p.purchase_invoice_id}`} />
              </div>
            ))}
            {pending.length > 0 && (
              <div className="text-right text-sm font-semibold pt-1 border-t" data-testid="supplier-op-alloc-total">Total a cancelar: {fmtCurrency(allocationsTotal)}</div>
            )}
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-semibold">Medios de Pago</Label>
              <Button type="button" variant="outline" size="sm" onClick={addPD} data-testid="supplier-op-add-payment"><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
            </div>
            {form.payment_details.map((pd, i) => {
              const ptName = paymentTypes.find(pt => pt.payment_type_id === pd.payment_type_id)?.name;
              return (
                <div key={i} className="mb-3 p-3 rounded-lg bg-muted/30">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <Label className="text-xs">Tipo de Valor *</Label>
                      <select value={pd.payment_type_id} onChange={(e) => updatePD(i, 'payment_type_id', e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" data-testid={`supplier-op-pt-${i}`}>
                        <option value="">Seleccionar...</option>
                        {availablePaymentTypes.map(pt => <option key={pt.payment_type_id} value={pt.payment_type_id}>{pt.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Monto *</Label>
                      <MoneyInput value={pd.amount} onChange={(e) => updatePD(i, 'amount', e.target.value)} placeholder="$ 0,00" className="h-9" data-testid={`supplier-op-pd-amount-${i}`} />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs">Referencia</Label>
                      <Input value={pd.reference} onChange={(e) => updatePD(i, 'reference', e.target.value)} placeholder="Nro cheque, transf..." className="h-9" />
                    </div>
                    <div className="col-span-1">
                      {form.payment_details.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removePD(i)} className="h-9 w-9 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                  {isDeferredCheck(ptName) && (
                    <div className="mt-2">
                      <Label className="text-xs text-amber-700 font-semibold">Fecha de pago del cheque diferido / e.Cheq *</Label>
                      <Input type="date" value={pd.due_date || ''} onChange={(e) => updatePD(i, 'due_date', e.target.value)} className="h-9 max-w-[200px] border-amber-300" data-testid={`supplier-op-due-${i}`} />
                      <p className="text-[11px] text-muted-foreground mt-1">Se generará un valor en VALORES (Pendiente) y no moverá fondos hasta marcarlo como Pagado.</p>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="text-right text-sm font-semibold mt-2">Total: {fmtCurrency(payTotal)}</div>
            {(() => {
              const advance = Math.round((payTotal - allocationsTotal) * 100) / 100;
              if (advance > 0.009 && payTotal > 0) {
                return (
                  <div className="text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2" data-testid="supplier-op-advance-banner">
                    💰 Sobran <strong>{fmtCurrency(advance)}</strong>: quedará como <strong>saldo a favor</strong> del proveedor para aplicar a próximos comprobantes.
                  </div>
                );
              }
              if (advance < -0.009) {
                return (
                  <div className="text-sm bg-red-50 border border-red-200 rounded px-3 py-2 mt-2" data-testid="supplier-op-overalloc-banner">
                    ⚠️ Lo asignado a comprobantes <strong>{fmtCurrency(allocationsTotal)}</strong> supera el total pagado <strong>{fmtCurrency(payTotal)}</strong>.
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div>
            <Label>Descripción</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
        </div>
        )}

        {!createdOP && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || submit.submitting} data-testid="supplier-op-save">
              {(saving || submit.submitting) ? 'Registrando…' : 'Registrar Pago'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
